/**
 * Checkout, client side. The mirror of backend/app/api/routes/payments.py.
 *
 * Three calls, in order:
 *
 *   createPaymentOrder()  the server prices the cart and registers the amount
 *                         with Razorpay; we get back an order id to open
 *                         Checkout with.
 *   openRazorpayCheckout() the shopper pays, inside Razorpay's own iframe.
 *   verifyPayment()       the server checks the signature and hands back the
 *                         confirmed order.
 *
 * Note what this module never sends: a total. The amount is computed server-side
 * and is only ever displayed here — a price shown in this app is a preview, and
 * the one charged comes from the catalogue on the server.
 *
 * `confirmFreeOrder` is the fourth path, for a cart that costs nothing: Razorpay
 * cannot take a zero payment, so those are fulfilled directly.
 */

import { USE_MOCKS } from '@/config';
import { forgetMockCartBooks } from './cart';
import { delay, request } from './client';
import { rememberMockOrder } from './orders';

/**
 * What a simulated payment is called. Mirrors MOCK_PAY_LABEL in
 * backend/app/services/payment_service.py — a mock-mode order has to be
 * identifiable as one on the receipt, whichever mock produced it.
 */
export const MOCK_PAY_LABEL = 'Simulated payment (test mode)';

/** Gateway config: which key to open Checkout with, and whether it is real money. */
export async function getPaymentConfig({ signal } = {}) {
  if (USE_MOCKS) {
    await delay(80);
    return {
      gateway: 'razorpay',
      label: 'Razorpay',
      description: 'Cards, UPI, Netbanking & Wallets',
      key_id: null,
      currency: 'INR',
      is_configured: false,
      mode: 'mock',
      declared_mode: 'dev',
      is_live: false,
      is_mock: true,
      is_misconfigured: false,
    };
  }
  return request('/api/payments/config', { signal });
}

/**
 * Prices the cart server-side and registers it with Razorpay.
 *
 * `payload.items` carries the full cart lines rather than just ids. The real
 * backend reads only {id, qty, languages} and reprices the rest — but the mock
 * below IS the backend when there isn't one, and it has no catalogue to price
 * from, so the lines travel with their prices attached.
 */
export async function createPaymentOrder(payload, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(300);
    return mockCreateOrder(payload);
  }
  return request('/api/payments/create-order', { method: 'POST', body: payload, signal });
}

/**
 * Verifies what Razorpay Checkout returned and returns the confirmed order.
 *
 * This is the call that decides whether a payment happened. Until it comes back
 * successfully the books are not bought, however convincing the Checkout modal
 * looked — the signature can only be checked where the secret is.
 */
export async function verifyPayment(response, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(300);
    return mockVerify(response);
  }
  return request('/api/payments/verify', { method: 'POST', body: response, signal });
}

/** Fulfils a cart with nothing to charge. The server re-prices before agreeing. */
export async function confirmFreeOrder(payload, { signal } = {}) {
  if (USE_MOCKS) {
    await delay(300);
    return mockFulfil(payload, { payLabel: 'No payment required' });
  }
  return request('/api/payments/confirm-free', { method: 'POST', body: payload, signal });
}

// ---------------------------------------------------------------------------
// Mock gateway — only when there is no backend at all (USE_MOCKS).
//
// It stands in for both this app's server AND Razorpay, so it mimics the one
// thing the real pair agree on: a `order_mock_…` id, which the checkout page
// recognises and offers to approve by hand instead of opening a Checkout modal
// that cannot exist. The backend's own mock mode (no Razorpay keys configured)
// produces exactly the same id, so the UI has one code path for both.
// ---------------------------------------------------------------------------

/** Pending mock orders, by mock order id. In memory: a reload abandons them, as it should. */
const pendingMockOrders = new Map();

function mockCreateOrder(payload) {
  const items = payload.items ?? [];
  const subtotal = items.reduce((sum, i) => sum + (i.lineTotal ?? i.price * i.qty), 0);
  // The mock stands in for the coupon service too: a code on the payload was
  // already priced by checkCoupon, so its discount travels with it.
  const discount = round2(payload.coupon_discount ?? 0);
  const total = round2(subtotal - discount);
  const orderId = `order_mock_${Math.random().toString(16).slice(2, 16)}`;

  pendingMockOrders.set(orderId, { payload, subtotal, discount, total });

  return {
    order_id: total > 0 ? orderId : null,
    amount: total,
    currency: items[0]?.currency ?? 'INR',
    razorpay_key_id: null,
    is_free: total <= 0,
    is_mock: true,
    receipt: `tubm_${Math.random().toString(16).slice(2, 14)}`,
    subtotal,
    discount,
    prefill_name: payload.buyerName ?? null,
    prefill_email: null,
    description: `${items.length} book${items.length === 1 ? '' : 's'} from TUBMarket`,
  };
}

function mockVerify(response) {
  const pending = pendingMockOrders.get(response.razorpay_order_id);
  if (!pending) {
    throw new Error('That payment has expired. Please try again.');
  }
  pendingMockOrders.delete(response.razorpay_order_id);
  return mockFulfil(pending.payload, {
    payLabel: MOCK_PAY_LABEL,
    subtotal: pending.subtotal,
    discount: pending.discount,
    total: pending.total,
  });
}

/** Builds the confirmation the real backend would have returned, and files it in history. */
function mockFulfil(payload, { payLabel, subtotal, discount = 0, total } = {}) {
  const items = payload.items ?? [];
  const resolvedSubtotal =
    subtotal ?? items.reduce((sum, i) => sum + (i.lineTotal ?? i.price * i.qty), 0);
  const requiresDelivery = items.some((i) => i.format === 'printed');

  const order = {
    id: `TUB-${Math.floor(10000 + Math.random() * 90000)}`,
    eta: requiresDelivery ? 'once your copy is dispatched' : 'right now in playTUB',
    items,
    subtotal: resolvedSubtotal,
    discount,
    total: total ?? round2(resolvedSubtotal - discount),
    currency: items[0]?.currency ?? 'INR',
    name: payload.address?.name ?? payload.buyerName ?? 'you',
    payLabel,
    payment_summary: payLabel,
    fulfilment: requiresDelivery ? 'delivery' : 'digital',
    username: payload.username ?? null,
    entitlements_recorded: true,
    status: 'placed',
    coupon_code: payload.coupon_code ?? null,
  };

  rememberMockOrder(order, payload.username);
  // Mirrors cart_service.remove_books: the books are bought, so the stored cart
  // must not still be holding them.
  forgetMockCartBooks(payload.username, items);
  return order;
}

const round2 = (n) => Math.round(n * 100) / 100;
